<template>
  <app-layout>
    <LoadingScreen :isLoading="loading"></LoadingScreen>
    <div class="flex flex-col p-4 items-center pt-16">
      <div class="w-full flex flex-col">
        <div
          class="flex flex-col md:flex-row lg:flex-row bg-white rounded-lg shadow p-6 flex flex-row justify-between items-center">
          <div class="w-full md:w-1/2 lg:w-1/3">
            <div class="flex space-x-2 items-center">
              <label class="font-semibold text-md">Periodo:</label>
              <button class="px-4 py-2 rounded border border-purple-500 transition-shadow" :class="{
                'bg-purple-500 text-white': active === 1,
                'bg-white text-purple-500': active !== 1,
                'cursor-not-allowed opacity-50': disabled,
                'hover:shadow-lg': !disabled,
              }" :disabled="disabled" @click="changeActive(1)">
                Hoy
              </button>
              <button class="px-4 py-2 rounded border border-purple-500 transition-shadow" :class="{
                'bg-purple-500 text-white': active === 2,
                'bg-white text-purple-500': active !== 2,
                'cursor-not-allowed opacity-50': disabled,
                'hover:shadow-lg': !disabled,
              }" :disabled="disabled" @click="changeActive(2)">
                Semana
              </button>
              <button class="px-4 py-2 rounded border border-purple-500 transition-shadow" :class="{
                'bg-purple-500 text-white': active === 3,
                'bg-white text-purple-500': active !== 3,
                'cursor-not-allowed opacity-50': disabled,
                'hover:shadow-lg': !disabled,
              }" :disabled="disabled" @click="changeActive(3)">
                Mes
              </button>
            </div>
          </div>
          <div class="w-full md:w-1/2 lg:w-1/3">
            <div class="flex space-x-2 items-center">
              <label class="font-semibold text-md">Personalizado:</label>
              <el-date-picker v-model="customRange" type="daterange" range-separator="a"
                start-placeholder="Fecha inicio" end-placeholder="Fecha fin" format="dd/MM/yyyy"
                value-format="yyyy-MM-dd" :disabled="disabled" class="w-64" @change="() => changeActive(0, true)" />
            </div>
          </div>
          <div class="flex flex-col md:flex-row md:items-end gap-4">
            <div class="w-full flex justify-end">
              <button class="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-2 rounded mt-4 md:mt-0"
                @click="generatePDF">
                Generar Reporte PDF
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <widgets id="widgets" :widgetsData="reportData" />
    <tabs :reportData="reportData" :widthChart="getWidthSizeWindow()" :isPrintMode="isPrintMode"
      :isMonthly="isMonthly" />
  </app-layout>
</template>

<script>
import numbers from "@/utils/numbers";
import Widgets from "./Widgets/Index.vue";
import Tabs from "./Tabs/Index.vue";
import ReportService from "@/services/ReportService";
import { MessageBox, Message } from "element-ui";
import { updateCompliance } from '@/utils/compliance';

export default {
  name: "FinancesIndex",
  components: {
    AppLayout: () => import("@/components/layouts/AppLayout"),
    LoadingScreen: () => import("@/components/layouts/LoadingScreen"),
    Widgets,
    Tabs,
  },
  mounted() {
    this.load();
    updateCompliance(this, 'reportsVisitedAt');
  },
  data() {
    return {
      isMonthly: true,
      isPrintMode: false,
      loading: true,
      businessInfoId: this.$store.state.business.id,
      activeTab: "finances",
      filters: {
        businessInfoId: this.$store.state.business.id,
        dueDateStart: this.$moment(new Date())
          .startOf("month")
          .format("MM/DD/YYYY"),
        dueDateEnd: this.$moment(new Date())
          .endOf("month")
          .format("MM/DD/YYYY"),
      },
      active: 3,
      customRange: [],
      reportData: {
        totalSales: 0,
        averageTicket: 0,
        totalOrdersCompleted: 0,
        averageTimeFromTicket: 0,
        monthsIncomes: [],
        monthsExpenses: [],
        profitability: [],
        technicalPerformance: [],
      },
    };
  },
  computed: {
    disabled() {
      return false; // Cambia esto según tu lógica para habilitar/deshabilitar los botones
    },
  },
  methods: {
    getWidthSizeWindow() {
      return 600;
    },
    moneyFormat(value) {
      const { currencySymbol } = this.$store.state.account;
      return numbers.moneyFormat(value, currencySymbol);
    },
    async changeActive(index, custom = false) {
      this.loading = true;
      this.active = index;
      await this.updateFilters(custom);
    },
    async updateFilters(custom = false) {

      if (custom) {
        // Validate that customRange has valid dates
        if (!this.customRange || this.customRange.length !== 2 || !this.customRange[0] || !this.customRange[1]) {
          this.loading = false;
          return; // Don't do anything if custom range is empty or invalid
        }

        this.filters.dueDateStart = this.$moment(this.customRange[0]).format(
          "MM/DD/YYYY"
        );
        this.filters.dueDateEnd = this.$moment(this.customRange[1]).format(
          "MM/DD/YYYY"
        );
        await this.load();
        return;
      }

      const init = new Date();
      const last = new Date();
      if (this.active === 1) {
        this.filters.dueDateStart = this.$moment(init).format("MM/DD/YYYY");
        this.filters.dueDateEnd = this.$moment(last).format("MM/DD/YYYY");
        this.isMonthly = false;
      }
      if (this.active === 2) {
        // Calcular el primer día de la semana (lunes) y el último día (domingo)
        const dayOfWeek = init.getDay();
        const diffToMonday = (dayOfWeek === 0 ? -6 : 1) - dayOfWeek;
        const firstDayOfWeek = new Date(init);
        firstDayOfWeek.setDate(init.getDate() + diffToMonday);
        const lastDayOfWeek = new Date(firstDayOfWeek);
        lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
        this.filters.dueDateStart =
          this.$moment(firstDayOfWeek).format("MM/DD/YYYY");
        this.filters.dueDateEnd =
          this.$moment(lastDayOfWeek).format("MM/DD/YYYY");
        this.isMonthly = false;
      }
      if (this.active === 3) {
        const month = init.getMonth();
        const year = init.getFullYear();
        this.filters.dueDateStart = this.$moment(
          new Date(year, month, 1)
        ).format("MM/DD/YYYY");
        this.filters.dueDateEnd = this.$moment(
          new Date(year, month + 1, 0)
        ).format("MM/DD/YYYY");
        this.isMonthly = true;
      }
      await this.load();
      return;
    },
    async load(print = false) {
      const data = await ReportService.get(this.filters);
      this.reportData = data;
      this.loading = false;
      this.customRange = [];
      this.customRange.push(
        this.$moment(this.filters.dueDateStart, "MM/DD/YYYY").toDate()
      );
      this.customRange.push(
        this.$moment(this.filters.dueDateEnd, "MM/DD/YYYY").toDate()
      );
      if (print) {
        setTimeout(() => {
          this.printPDF();
        }, 2000);
      }
    },
    generatePDF() {
      const now = new Date();
      const nowMonth = now.getMonth() + 1;
      const nowYear = now.getFullYear();

      const monthNames = [
        "Enero",
        "Febrero",
        "Marzo",
        "Abril",
        "Mayo",
        "Junio",
        "Julio",
        "Agosto",
        "Septiembre",
        "Octubre",
        "Noviembre",
        "Diciembre",
      ];

      let monthOptions = "";
      for (let m = 1; m <= 12; m++) {
        const selected = m === nowMonth ? "selected" : "";
        monthOptions += `<option value="${m}" ${selected}>${monthNames[m - 1]
          }</option>`;
      }

      // Rango de años: del año actual hacia atrás 5 años (ajustable)
      let yearsOptions = "";
      const yearsBack = 5;
      for (let y = nowYear; y >= nowYear - yearsBack; y--) {
        const selected = y === nowYear ? "selected" : "";
        yearsOptions += `<option value="${y}" ${selected}>${y}</option>`;
      }

      const html = `
        <div style="display:flex; gap:12px; align-items:center;">
          <div>
        <label style="display:block; font-weight:600; margin-bottom:6px;">Mes</label>
        <select id="report-month" style="padding:6px 8px;">${monthOptions}</select>
          </div>
          <div>
        <label style="display:block; font-weight:600; margin-bottom:6px;">Año</label>
        <select id="report-year" style="padding:6px 8px;">${yearsOptions}</select>
          </div>
        </div>
      `;

      MessageBox.confirm(html, "Seleccionar periodo", {
        dangerouslyUseHTMLString: true,
        confirmButtonText: "Generar",
        cancelButtonText: "Cancelar",
      })
        .then(() => {
          const month = parseInt(
            document.getElementById("report-month").value,
            10
          );
          const year = parseInt(
            document.getElementById("report-year").value,
            10
          );

          // Validar que la fecha seleccionada sea menor o igual al mes actual
          if (year > nowYear || (year === nowYear && month > nowMonth)) {
            Message.error(
              "El mes y año deben ser menor o igual al mes actual."
            );
            this.loading = false;
            this.isPrintMode = false;
            return;
          }

          // Establecer filtros: inicio = primer día del mes, fin = último día del mes
          const startDate = this.$moment(new Date(year, month - 1, 1)).format(
            "MM/DD/YYYY"
          );
          const endDate = this.$moment(new Date(year, month, 0)).format(
            "MM/DD/YYYY"
          );

          this.filters.dueDateStart = startDate;
          this.filters.dueDateEnd = endDate;

          this.loading = true;
          this.isPrintMode = true;
          // Cargar datos y luego imprimir
          this.load(true);
        })
        .catch(() => {
          // Usuario canceló el diálogo
          this.loading = false;
          this.isPrintMode = false;
        });
    },
    printPDF() {
      const el = document.getElementById("widgets");
      const summaryEl = document.getElementById("report-summary");
      const incomeEl = document.getElementById("report-income");
      const expenses1El = document.getElementById("report-expenses1");
      const expenses2El = document.getElementById("report-expenses2");
      const operations1El = document.getElementById("report-operations1");
      const operations2El = document.getElementById("report-operations2");
      const profitabilityEl = document.getElementById("report-profitability");
      const staticEl = document.getElementById("report-statics");
      const top5ClientsEl = document.getElementById("report-top5-clients");
      if (!el) {
        this.loading = false;
        this.isPrintMode = false;
        alert("Error al generar el reporte. Intente de nuevo.");
        return;
      }
      const missing = [];
      const checks = [
        [summaryEl, "Resumen"],
        [incomeEl, "Ingresos"],
        [expenses1El, "Gastos 1"],
        [expenses2El, "Gastos 2"],
        [operations1El, "Operaciones 1"],
        [operations2El, "Operaciones 2"],
        [profitabilityEl, "Rentabilidad"],
        [staticEl, "Estadísticas"],
        [top5ClientsEl, "Top 5 Clientes"],
      ];
      checks.forEach(([el, name]) => {
        if (!el) missing.push(name);
      });
      if (missing.length) {
        const msg = `No se pudieron generar las siguientes secciones/gráficas: ${missing.join(
          ", "
        )}`;
        console.error(msg, { missing });
        alert(msg);
        this.loading = false;
        this.isPrintMode = false;
        return;
      }
      const newWindow = window.open("", "_blank");
      // Si la ventana emergente fue bloqueada, newWindow será null; evitar errores y notificar al usuario.
      if (!newWindow || !newWindow.document) {
        alert(
          "Se ha bloqueado la ventana emergente. Por favor permita pop-ups para generar el reporte o intente de nuevo."
        );
        this.loading = false;
        this.isPrintMode = false;
        return;
      }
      const headHTML = document.head.innerHTML;
      const bodyHTML = el.outerHTML;
      const summaryHTML = summaryEl.outerHTML;
      newWindow.document.write(
        `<!doctype html>
        <html>
        <head>${headHTML}
          <title>Reporte Financiero</title>
          </head>
          <body>
            <h1 class="text-purple-700 text-2xl font-semibold" style="color:#6B21A8">Reporte Financiero</h1>
            <h3>Generado el ${this.$moment().format("DD/MM/YYYY")}</h3>
            ${bodyHTML}

              <div style="max-width: 794px; margin: 0 auto 50px; display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; page-break-after: always; page-break-inside: avoid; break-after: page; break-inside: avoid;">
                ${summaryHTML}
              </div>

              <div style="max-width: 794px; margin: 0 auto 50px; display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; page-break-after: always; page-break-inside: avoid; break-after: page; break-inside: avoid;">

                ${incomeEl.outerHTML}
              </div>

              <div style="max-width: 794px; margin: 0 auto 50px; display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; page-break-after: always; page-break-inside: avoid; break-after: page; break-inside: avoid;">
              ${expenses1El.outerHTML}
              ${expenses2El.outerHTML}
              </div>

              <div style="max-width: 794px; margin: 0 auto 50px; display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; page-break-after: always; page-break-inside: avoid; break-after: page; break-inside: avoid;">
              ${operations1El.outerHTML}
              ${operations2El.outerHTML}
              </div>

              <div style="max-width: 794px; margin: 0 auto 50px; display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; page-break-after: always; page-break-inside: avoid; break-after: page; break-inside: avoid;">

                ${profitabilityEl.outerHTML}
              </div>
              <div style="max-width: 794px; margin: 0 auto 50px; display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; page-break-inside: avoid;">
              ${staticEl.outerHTML}
              </div>
              <div style="max-width: 794px; margin: 0 auto 50px; display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; page-break-inside: avoid;">
              ${top5ClientsEl.outerHTML}
              </div>
          </body>
        </html>`
      );
      newWindow.document.close();
      newWindow.focus();
      // Esperar un poco para que se apliquen estilos y fuentes externas
      setTimeout(() => {
        newWindow.print();
        // opcional: cerrar la ventana después de imprimir
        newWindow.close();
        this.isPrintMode = false;
        this.loading = false;
      }, 500);
    },
  },
};
</script>

<style scoped>
.reports-index {
  margin-top: 40px;
}
</style>
